import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import type { DivisionRef, ThemeColors } from '../services/api'
import { applyThemeToDOM, applyFaviconToDOM, DEFAULT_THEME } from '../utils/theme'
import { sectionEnabled, isSectionKey, isMembersOnly, tenantNoindex, type RegistrationSiteKey, type TenantSiteSections } from './tenantAccess'
import { setPrivateSite } from '../services/publicAccess'
import { usePreviewTenant } from './PreviewBridge'

export interface TenantBranding {
  hero_image_url: string | null
  og_image_url: string | null
  footer_text: string | null
}

export interface TenantCopy {
  blog_intro: string | null
  gallery_intro: string | null
  hero_tagline: string | null
  community_quote: string | null
}

export type TenantFeatures = Record<string, boolean>

// Switch-model access helpers (site sections, add-ons, account status) live
// in ./tenantAccess.ts — a plain module, so this file keeps exporting only
// the provider and hooks for React fast refresh. The types are re-exported
// here for convenience.
export type { SiteSectionKey, TenantSiteSections, AddonKey } from './tenantAccess'

export interface TenantSectionOverride {
  title?: string | null
  content?: any
}

export type TenantSectionOverrides = Record<string, TenantSectionOverride>

// "Powered by ScoreLM" attribution toggles (#60). Controls where the
// platform credit appears for this tenant. Defaults when the field is
// absent: footer ON, share-card watermark OFF, corner badge OFF — read
// these via attributionEnabled() rather than the raw object.
export interface TenantAttribution {
  footer: boolean
  share_cards: boolean
  corner_badge: boolean
}

export const DEFAULT_ATTRIBUTION: TenantAttribution = {
  footer: true,
  share_cards: false,
  corner_badge: false,
}

export interface TenantSeasonSummary {
  id: number
  name: string | null
  year: number
  sport_id: string
  is_active: boolean
  // #157: the one season per (league, sport) the public site defaults to.
  // #583: per (league, sport, division) once a sport has divisions.
  is_current?: boolean
  /** #583: the season's division (null = none). Absent on older payloads. */
  division_id?: number | null
}

/**
 * #583: a non-archived division of one sport (docs/divisions/API.md §4.1).
 * "Live" (shown publicly) means `public_season_id != null`; the public switcher
 * appears only when the active sport has 2+ live divisions.
 */
export interface TenantDivision extends DivisionRef {
  sport_id: string
  /** Admin order; the first live division is the sport's default division. */
  display_order: number
  /** The division's public season (the shared resolver), or null (not live). */
  public_season_id: number | null
  /** Active non-utility seasons, newest first. */
  active_season_ids: number[]
}

// Subscription state (Phase 1). `is_live` gates whether the public site is
// served at all; `entitlements` lists the feature keys the tenant is granted.
// Both are optional on the payload — older backends / still-loading state
// omit them, in which case enforcement defaults GRACEFULLY to "entitled".
export interface TenantSubscription {
  status: string
  is_live: boolean
  trial_ends_at: string | null
  current_period_end: string | null
}

export interface TenantInfo {
  id: number
  name: string
  subdomain: string | null
  custom_domain: string | null
  custom_domain_verified_at: string | null
  redirect_to_custom: boolean
  is_active: boolean
  logo_url: string | null
  theme: ThemeColors
  favicon_url: string | null
  branding: TenantBranding
  copy: TenantCopy
  /** DEPRECATED (one release): mirrors site_sections. Read sectionEnabled(). */
  features: TenantFeatures
  section_overrides: TenantSectionOverrides
  /** DEPRECATED (one release): mirrors site_sections.venues. Read sectionEnabled(). */
  venues_page_enabled: boolean
  /**
   * Resolved site-section visibility for the applied sport. Optional: older
   * payloads (and cached localStorage tenants) omit it, in which case
   * sectionEnabled() falls back to `features` / `venues_page_enabled`.
   */
  site_sections?: Partial<TenantSiteSections>
  /** Entitled add-on keys (subset of ADDON_KEYS). Optional on older payloads. */
  addons?: string[]
  // Multi-sport (#35) — `default_sport_id` is the tenant's primary sport;
  // every season carries its own `sport_id` (which may override the default
  // for that competition). The frontend uses these to pick the right sport
  // pack for the active page.
  default_sport_id: string
  /** i18n (#1063): the language the site and admin open in ('en' | 'fr' | 'es'). Optional on older payloads (English). */
  default_locale?: string
  /** The languages fans may switch to (the switch shows at 2+). Optional on older payloads (English only). */
  enabled_locales?: string[]
  seasons: TenantSeasonSummary[]
  /**
   * #583: non-archived divisions of every sport, ordered by sport, then
   * display_order, then id. Undefined (older payloads and cached tenants)
   * means []. Read through `site/core/division` (liveDivisions), never raw.
   */
  divisions?: TenantDivision[]
  /**
   * #568: the sports the league has enabled (league_sports.is_enabled), the
   * same for every ?sport=. A disabled or archived sport keeps its seasons,
   * but is not public: it is filtered out of the tenant's sport list
   * (`tenantSportIds`). Optional on older payloads (then every season's
   * sport counts, as before).
   */
  enabled_sport_ids?: string[]
  /**
   * Which sport scope the backend merged into this payload. `null` when
   * the request didn't pass ?sport= (single-sport tenants, or first load
   * on a multi-sport tenant). When set, theme/copy/branding reflect that
   * sport's per-sport overrides on top of the league-wide defaults.
   */
  applied_sport_id?: string | null
  /**
   * "Powered by ScoreLM" attribution toggles (#60). Optional — older
   * payloads omit it. Read individual placements via attributionEnabled()
   * so missing keys fall back to the per-placement defaults.
   */
  attribution?: TenantAttribution
  /**
   * Subscription enforcement (Phase 2). Optional — older payloads omit these.
   * `entitlements` is the set of feature keys this tenant is currently allowed
   * (includes all CORE features when the subscription is live). When the field
   * is absent or undefined, treat everything as entitled so nothing breaks for
   * older payloads / still-loading state. `subscription.is_live === false`
   * pauses the public site (see App.tsx → SubscriptionInactivePage).
   */
  entitlements?: string[]
  subscription?: TenantSubscription
  /**
   * The league's website template (site templates §9.5), league-wide,
   * resolved by the backend with defaults filled in. Optional: older
   * payloads and cached tenants omit it, which means 'classic'.
   */
  site_template?: { id: string; options: Record<string, string | boolean> }
  /**
   * Who may see the public site (registration step 32): 'members_only' puts
   * the site behind the sign-in wall (step 33, SiteAccessProvider). Optional
   * because an older cached `mcl_tenant_<host>` copy may lack it; a missing
   * value means public. The server's members-only gate is the control; this
   * only decides what the site draws and whether it sends a Bearer.
   */
  visibility?: 'public' | 'members_only'
  /**
   * How registration shows on the site (Website › Design › Registration,
   * owner October 28): league-wide, each switch on unless the league turned
   * it off. Optional: an older cached payload lacks it, which means all on.
   * Only read while the registration add-on is on.
   */
  registration_site?: Partial<Record<RegistrationSiteKey, boolean>>
}

// Legacy feature-toggle defaults, kept for older call sites. The section
// keys now resolve through sectionEnabled(); rules/sponsors were dropped
// (#334) because nothing ever read them.
export const DEFAULT_FEATURES: TenantFeatures = {
  schedule: true,
  players: true,
  teams: true,
  stats: true,
  gallery: true,
  blog: true,
}

// Whether the tenant's subscription currently grants `key`. Defaults
// GRACEFULLY: a missing/undefined `entitlements` array (older payload or
// still-loading) means "everything entitled" so enforcement is a no-op.
export function featureEntitled(tenant: TenantInfo | null, key: string): boolean {
  const entitlements = tenant?.entitlements
  if (!entitlements) return true
  return entitlements.includes(key)
}

// Compatibility shim. Section keys (schedule/players/teams/stats/gallery/
// blog/venues) resolve through sectionEnabled(): they are core now, so no
// entitlement check applies. Any other key falls back to the entitlement
// check plus the legacy toggle.
export function featureEnabled(tenant: TenantInfo | null, key: string): boolean {
  if (isSectionKey(key)) return sectionEnabled(tenant, key)
  if (!tenant) return DEFAULT_FEATURES[key] ?? true
  if (!featureEntitled(tenant, key)) return false
  if (tenant.features?.[key] === false) return false
  return true
}

// "Powered by ScoreLM" attribution placement check (#60). When the tenant
// payload omits the `attribution` field (or the specific key), fall back to
// the per-placement default: footer ON, share_cards/corner_badge OFF.
export function attributionEnabled(
  tenant: TenantInfo | null,
  key: keyof TenantAttribution
): boolean {
  const value = tenant?.attribution?.[key]
  return typeof value === 'boolean' ? value : DEFAULT_ATTRIBUTION[key]
}

function ensureMeta(name: string, attr: 'name' | 'property' = 'name'): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  return el
}

function applyHeadMetadata(tenant: TenantInfo) {
  // A public page that titled itself ("Schedule · League", #818) keeps its title.
  if (tenant.name && !document.documentElement.hasAttribute('data-page-title')) document.title = tenant.name

  const description = tenant.copy?.hero_tagline || `${tenant.name} — official site`
  ensureMeta('description').content = description

  ensureMeta('og:title', 'property').content = tenant.name
  ensureMeta('og:description', 'property').content = description
  ensureMeta('og:type', 'property').content = 'website'

  const ogImage = tenant.branding?.og_image_url || tenant.branding?.hero_image_url || tenant.logo_url
  if (ogImage) ensureMeta('og:image', 'property').content = ogImage

  ensureMeta('twitter:card').content = ogImage ? 'summary_large_image' : 'summary'
  ensureMeta('twitter:title').content = tenant.name
  ensureMeta('twitter:description').content = description
  if (ogImage) ensureMeta('twitter:image').content = ogImage

  // Point the manifest <link> at the per-tenant manifest endpoint so PWA
  // install / browser theme-color matches this tenant.
  let manifestLink = document.head.querySelector<HTMLLinkElement>("link[rel='manifest']")
  if (!manifestLink) {
    manifestLink = document.createElement('link')
    manifestLink.rel = 'manifest'
    document.head.appendChild(manifestLink)
  }
  manifestLink.href = `${import.meta.env.VITE_API_URL || '/api/v1'}/public/manifest.json`

  if (tenant.theme?.bgPrimary) {
    ensureMeta('theme-color').content = tenant.theme.bgPrimary
  }

  // #1386: trial sites are noindex until the league converts.
  if (tenantNoindex(tenant)) ensureMeta('robots').content = 'noindex'
  else document.head.querySelector('meta[name="robots"]')?.remove()
}

// Routes we never 301 to the custom domain — admin needs operational access on
// the scorelm.com subdomain, OBS scorebugs have their own embed URLs, and the
// API path is on the backend host anyway.
const NON_REDIRECTABLE_PATH_PREFIXES = ['/admin', '/obs', '/api']

function shouldRedirectToCustom(tenant: TenantInfo): string | null {
  if (!tenant.redirect_to_custom) return null
  if (!tenant.custom_domain || !tenant.custom_domain_verified_at) return null

  const currentHost = window.location.hostname.toLowerCase()
  const targetHost = tenant.custom_domain.toLowerCase()
  if (currentHost === targetHost) return null

  const path = window.location.pathname
  if (NON_REDIRECTABLE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null
  }

  return `https://${targetHost}${window.location.pathname}${window.location.search}${window.location.hash}`
}

interface LeagueContextType {
  league: TenantInfo | null
  isLoading: boolean
  error: string | null
  // Force a fresh fetch of /public/tenant. Admin pages call this after
  // saving Customization changes so the public site (or other open tabs)
  // pick up the change immediately instead of waiting for the next full
  // page load.
  refreshTenant: () => Promise<void>
  // Multi-sport (#35): SportContext calls this when the fan flips sports
  // on a multi-sport tenant so theme/copy/branding overrides take effect.
  reloadForSport: (sportId: string | null) => Promise<void>
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined)

const TENANT_CACHE_PREFIX = 'mcl_tenant_'

function getCacheKey(): string {
  return `${TENANT_CACHE_PREFIX}${window.location.hostname}`
}

function getCachedTenant(): TenantInfo | null {
  try {
    const cached = localStorage.getItem(getCacheKey())
    if (cached) {
      return JSON.parse(cached) as TenantInfo
    }
  } catch {
    // Ignore localStorage errors
  }
  return null
}

function cacheTenant(tenant: TenantInfo) {
  try {
    localStorage.setItem(getCacheKey(), JSON.stringify(tenant))
  } catch {
    // Ignore localStorage errors
  }
}

function applyTenantBranding(tenant: TenantInfo) {
  if (tenant.theme) {
    applyThemeToDOM(tenant.theme)
  }
  if (tenant.favicon_url) {
    applyFaviconToDOM(tenant.favicon_url)
  }
  applyHeadMetadata(tenant)
}

// Apply cached tenant branding immediately on module load to avoid flash
const cachedOnBoot = getCachedTenant()
if (cachedOnBoot) {
  applyTenantBranding(cachedOnBoot)
} else {
  applyThemeToDOM(DEFAULT_THEME)
}

export function LeagueProvider({ children }: { children: ReactNode }) {
  const [league, setLeague] = useState<TenantInfo | null>(cachedOnBoot)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The private-site slot for the public client (registration step 33,
  // services/publicAccess.ts). DELIBERATELY SET DURING RENDER (not in a
  // useEffect), like SportProvider's sport slot: React commits child effects
  // before parent effects, so an effect-deferred write would let page fetches
  // go out without the Bearer (or with it on a league that just went public).
  // Setting it during render guarantees the slot is current the moment any
  // child sees the new tenant. `league`, not the preview overlay: drafts never
  // carry visibility.
  setPrivateSite(isMembersOnly(league))

  // Always force-fetch (no HTTP cache). Used both on mount and by
  // refreshTenant() after admin saves. Returns true if the tenant was
  // updated; false on a 404 / network error.
  //
  // Multi-sport (#35): pass ?sport=X so the backend merges per-sport
  // setting overrides (theme, copy, branding, etc.) on top of the
  // league-wide defaults. Single-sport tenants pass nothing.
  const fetchTenant = useCallback(async (sportId?: string | null): Promise<boolean> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'
    const qs = sportId ? `?sport=${encodeURIComponent(sportId)}` : ''
    try {
      const response = await window.fetch(`${API_BASE_URL}/public/tenant${qs}`, { cache: 'no-store' })
      if (response.status === 404) {
        setError('League not found')
        return false
      }
      if (!response.ok) {
        setError(`Failed to load league (status ${response.status})`)
        return false
      }
      const tenant = (await response.json()) as TenantInfo

      const redirectTarget = shouldRedirectToCustom(tenant)
      if (redirectTarget) {
        window.location.replace(redirectTarget)
        return false
      }

      applyTenantBranding(tenant)
      cacheTenant(tenant)
      setLeague(tenant)
      setError(null)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load league')
      return false
    }
  }, [])

  const refreshTenant = useCallback(async () => {
    await fetchTenant()
  }, [fetchTenant])

  const reloadForSport = useCallback(async (sportId: string | null) => {
    await fetchTenant(sportId)
  }, [fetchTenant])

  useEffect(() => {
    let cancelled = false
    fetchTenant().finally(() => {
      if (!cancelled) setIsLoading(false)
    })
    return () => { cancelled = true }
  }, [fetchTenant])

  // Cross-tab sync: when one tab updates the tenant cache (e.g., after
  // an admin save), other tabs on the same hostname pick up the change
  // automatically via the storage event.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== getCacheKey() || !e.newValue) return
      try {
        const next = JSON.parse(e.newValue) as TenantInfo
        applyTenantBranding(next)
        setLeague(next)
      } catch {}
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Admin-v2 (Website › Design): a draft laid over the tenant inside the
  // admin's preview frame only, and a refetch when the admin saves something
  // the tenant payload carries (name, logo, favicon, a published theme).
  const shownLeague = usePreviewTenant(league)
  useEffect(() => {
    const onRefresh = () => void fetchTenant()
    window.addEventListener('scorelm:tenant-refresh', onRefresh)
    return () => window.removeEventListener('scorelm:tenant-refresh', onRefresh)
  }, [fetchTenant])

  return (
    <LeagueContext.Provider value={{ league: shownLeague, isLoading, error, refreshTenant, reloadForSport }}>
      {children}
    </LeagueContext.Provider>
  )
}

export function useLeague() {
  const context = useContext(LeagueContext)
  if (context === undefined) {
    throw new Error('useLeague must be used within a LeagueProvider')
  }
  return context
}
